import React, { useState, useEffect } from 'react';
import { EditButton, Edit, SimpleForm, TextInput, ReferenceInput, SelectInput, NumberInput, useNotify, useRefresh, Confirm } from 'react-admin';
import { Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import SortableTable from './components/SortableTable';
import { httpClient, publicBase } from './dataProvider';

const DeleteCourseResultButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const url = `${publicBase}/course_results/${record.id}`;
            await httpClient(url, { method: 'DELETE' });
            notify('Course result deleted successfully', { type: 'success' });
            refresh();
            window.location.reload();
        } catch (error) {
            console.error('Error deleting course result:', error);
            notify('Failed to delete course result', { type: 'error' });
        } finally {
            setLoading(false);
            setOpen(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleClick}
                color="error"
                startIcon={<DeleteIcon />}
            >
                Delete
            </Button>
            <Confirm
                isOpen={open}
                loading={loading}
                title="Delete Course Result"
                content="Are you sure you want to delete this course result? This action cannot be undone."
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
            />
        </>
    );
};

export const CourseResultList = () => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchResults = () => {
        const url = `${publicBase}/course_results?end=1000`; // Fetch all for client-side sort
        setLoading(true);
        httpClient(url)
            .then(({ json }) => {
                setResults(json || []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching course results:', error);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchResults();
    }, []);

    const columns = [
        { key: 'user_email', header: 'User Email' },
        { key: 'course_title', header: 'Course Title' },
        { key: 'company_name', header: 'Company' },
        { key: 'role_at_company', header: 'Role' },
        { key: 'score', header: 'Score' },
        {
            key: 'completed_at',
            header: 'Completed At',
            render: (record) => new Date(record.completed_at).toLocaleString()
        },
        { key: 'authenticity_code', header: 'Authenticity Code' },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <EditButton record={record} resource="course_results" />
                    <DeleteCourseResultButton record={record} />
                </div>
            )
        }
    ];

    if (loading) return <p>Loading...</p>;

    return (
        <div>
            <h2>Course Results</h2>
            <SortableTable data={results} columns={columns} />
        </div>
    );
};

// CourseResults are typically system-generated, so we provide a read-only-ish Edit view
// and no Create view.
export const CourseResultEdit = () => (
    <Edit title="Edit Course Result">
        <SimpleForm>
            <h2>Course Results</h2>
            <ReferenceInput source="user_id" reference="users">
                <SelectInput optionText="email" disabled />
            </ReferenceInput>
            <ReferenceInput source="course_id" reference="courses">
                <SelectInput optionText="title" disabled />
            </ReferenceInput>
            <NumberInput source="score" />
            <TextInput source="authenticity_code" disabled />
        </SimpleForm>
    </Edit>
);
